import Button from "~/components/ui/Button";
import Icon from "~/components/ui/Icon";
import Image from "~/components/ui/Image";
import { useSuggestions } from "~/sdk/useSuggestions";
import { useStore } from "@tanstack/react-store";
import React, { useEffect, useRef, useState } from "react";
import { Props as FloatingSearchButtonProps } from "~/components/search/FloatingSearchButton";
import { useId } from "react";
import { sendEvent } from "~/sdk/analytics";
import { Signal } from "~/sdk/signal";
import type { MenuNavigationItem } from "~/components/Header/types";
import NavItem from "~/components/Header/NavItem";
import MyAccount from "~/components/Header/Buttons/MyAccount";
import CartButtonVTEX from "~/components/Header/Buttons/Cart/vtex";
import type { B2BButton } from "~/components/Header/Navbar";
import { sanitizeStoreUrl } from "~/utils/constants";

export interface Props extends FloatingSearchButtonProps {
  isSearchVisible: Signal<boolean>;
  items: MenuNavigationItem[];
  translucentEffect?: boolean;
  b2bButton?: B2BButton;
  platform?: string;
  logo?: {
    src: string;
    alt: string;
    logoWidth?: number;
    logoHeight?: number;
    url?: string;
  };
}

function DesktopSearchbar({
  placeholder = "Busca",
  action = "/s",
  name = "q",
  loader,
  isSearchVisible,
  items,
  translucentEffect,
  platform,
  logo,
}: Props) {
  const id = useId();
  const searchType = "desktop";
  const searchVisible = useStore(isSearchVisible.store, (s) => s);
  const [openNavIndex, setOpenNavIndex] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { setQuery, payload, loading } = useSuggestions(loader);
  const payloadValue = useStore(payload.store, (s) => s);
  const loadingValue = useStore(loading.store, (s) => s);
  const { products = [], searches = [] } = payloadValue ?? {};
  const suggestedProducts = products ?? [];
  // Referência ou nome sem histórico de busca (ex: "8348E") só tem produtos.
  const hasTerms = Boolean(searches.length || suggestedProducts.length);
  const suggestionClass = `suggestions-${searchType}`;
  const suggestionId = `search-suggestion-${searchType}-${id}`;

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [queryText, setQueryText] = useState("");

  // Busca os termos mais populares assim que a caixa de busca abre, antes
  // mesmo do usuário digitar — o loader (suggestionsWithTopSearches)
  // retorna os termos reais mais buscados quando a query é vazia.
  useEffect(() => {
    if (searchVisible) {
      setQuery("");
    } else {
      setQueryText("");
    }
  }, [searchVisible]);

  // Reset search state when component becomes visible again
  useEffect(() => {
    const handleScroll = () => {
      if (globalThis.scrollY === 0 && isSearchVisible.value) {
        isSearchVisible.value = false;
        setIsInputFocused(false);
      }
    };

    globalThis.addEventListener("scroll", handleScroll);
    return () => globalThis.removeEventListener("scroll", handleScroll);
  }, []);

  const handleBlur = (e: React.FocusEvent<HTMLFormElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;

    if (
      relatedTarget && relatedTarget.closest(`.${suggestionClass}`) !== null
    ) {
      return;
    }
    setIsInputFocused(false);
  };

  return (
    <div
      className={searchVisible
        ? "flex items-center justify-between w-full gap-4 2xl:gap-8"
        : "grid grid-cols-[minmax(180px,1fr)_auto_minmax(180px,1fr)] items-center w-full gap-4 2xl:gap-8"}
    >
      {logo && (
        <a
          href={logo.url || "/"}
          className="shrink-0 inline-flex items-center"
          aria-label="Store logo"
        >
          <Image
            loading="eager"
            fetchPriority="high"
            className="logoImage"
            src={logo.src}
            alt={logo.alt}
            width={logo.logoWidth ?? 130}
            height={logo.logoHeight ?? 37}
          />
        </a>
      )}
      {/* Área central - Alterna entre menu e busca. Fora do modo de busca,
          esta é a coluna do meio de um grid `minmax(180px,1fr) auto minmax(180px,1fr)`:
          as duas colunas laterais (logo e ícones) sempre dividem o espaço
          restante em partes IGUAIS entre si (o mesmo piso mínimo nos dois
          lados remove a assimetria — com `1fr` puro, cada coluna partia da
          largura do seu próprio conteúdo, e como o bloco de ícones é bem
          mais largo que a logo, sobrava mais espaço do lado dos ícones e o
          menu ficava deslocado pra esquerda). Assim o menu fica de fato
          centralizado na largura total do header, como no Lança Perfume
          (referência pedida pela JDD). O piso de 180px (em vez de 0) garante
          que a coluna dos ícones nunca fique mais estreita que o conteúdo
          real do bloco de busca/favoritos/conta/carrinho (~168px) — abaixo
          disso os ícones, alinhados à direita, transbordavam por cima dos
          últimos itens do menu (achado 02 da auditoria de 23/08/2026). */}
      {!searchVisible
        ? (
          <ul className="flex gap-3 2xl:gap-5 justify-self-center justify-center h-12 mb-0 listCategories">
            {items.map((item, index) => (
              <NavItem
                key={`nav-item-${item.name || index}`}
                item={item}
                isOpen={openNavIndex === index}
                onToggle={() =>
                  setOpenNavIndex(openNavIndex === index ? null : index)}
                onOpen={() => setOpenNavIndex(index)}
                onClose={() =>
                  setOpenNavIndex((curr) => (curr === index ? null : curr))}
              />
            ))}
          </ul>
        )
        : (
            <div
              className={`flex-1 w-full transition-all duration-300 flex items-center justify-between relative`}
            >
              <form
                id={id}
                action={action}
                onFocus={() => setIsInputFocused(true)}
                onBlur={handleBlur}
                className="flex h-[38px] items-center w-full justify-center border-b-[1px] border-[#1A1A1A]"
              >
                <Button
                  type="submit"
                  className="h-[38px] w-[1%] mr-2"
                  aria-label="Busca"
                  tabIndex={-1}
                >
                  {loadingValue
                    ? <span className="loading loading-spinner loading-xs" />
                    : (
                      <Icon
                        id="MagnifyingGlass"
                        size={24}
                        strokeWidth={0.01}
                      />
                    )}
                </Button>
                <input
                  ref={searchInputRef}
                  id={`search-input-desktop-${id}`}
                  className="bg-transparent border-none rounded-none font-['Poppins'] text-base w-[99%] px-2.5"
                  name={name}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setQueryText(value);
                    if (value) {
                      sendEvent({
                        name: "search",
                        params: { search_term: value },
                      });
                    }
                    setQuery(value);
                  }}
                  placeholder={placeholder}
                  role="combobox"
                  aria-labelledby={`search-label-${id}`}
                  aria-describedby={`search-description-${id}`}
                  aria-controls={`search-suggestion-desktop-${id}`}
                  aria-autocomplete="list"
                  autoFocus
                />
                <span id={`search-label-${id}`} className="sr-only">
                  Search
                </span>
                <span id={`search-description-${id}`} className="sr-only">
                  Enter your search term
                </span>
              </form>

              <div
                className={`${suggestionClass} absolute bg-white top-[45px] left-0 right-0 rounded-2.5 shadow-lg z-50 max-h-[600px] ${
                  isInputFocused && hasTerms ? "" : "hidden"
                }`}
              >
                {!queryText && hasTerms && (
                  <p className="px-2.5 pt-2.5 pb-1 text-xs font-semibold uppercase tracking-wide text-[#333333]">
                    Mais buscados
                  </p>
                )}
                <ul
                  id={suggestionId}
                  className="flex flex-col w-full p-[2.5px] font-['Poppins'] text-sm max-h-[600px] overflow-auto"
                >
                  {searches.map(({ term }, index) => (
                    <li
                      key={`search-${index}`}
                      className="group p-2.5 min-h-10 hover:bg-[#C18A58]"
                    >
                      <a
                        className="flex flex-row items-center w-full text-black group-hover:text-white"
                        href={`/s?q=${term}`}
                      >
                        <span>{term}</span>
                      </a>
                    </li>
                  ))}
                  {suggestedProducts.map((product, index) => (
                    <li
                      key={`product-${product.url || index}`}
                      className="group p-2.5 w-full min-h-10 hover:bg-[#C18A58]"
                    >
                      <a
                        className="flex flex-row items-center gap-[5px] w-full text-black group-hover:text-white"
                        href={sanitizeStoreUrl(product.url)}
                      >
                        {product?.image && product?.image?.length && (
                          <Image
                            width={25}
                            height={25}
                            src={product.image[0].url ?? ""}
                            alt={product.isVariantOf?.name}
                            loading="lazy"
                            fetchPriority="low"
                          />
                        )}
                        <span>
                          {product?.name && product?.isVariantOf?.name
                            ? product?.isVariantOf?.name?.replace(
                              product?.name,
                              "",
                            )
                            : ""}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

      {/* Ícones à direita - Sempre visíveis */}
      <div className="flex items-center gap-4 justify-self-end">
        {/* Busca em destaque: fundo sólido pra se diferenciar visualmente
            dos demais ícones (que são links de texto/ghost simples). */}
        <Button
          className="group flex items-center justify-center bg-[#1A1A1A] text-white rounded-full w-10 h-10 hover:bg-[#C18A58] hover:text-[#1A1A1A] transition-colors"
          aria-label="Exibir busca"
          onClick={() => {
            isSearchVisible.value = !isSearchVisible.value;
          }}
        >
          <Icon
            id={searchVisible ? "XMark" : "MagnifyingGlass2"}
            size={searchVisible ? 20 : 16}
            strokeWidth={searchVisible ? 2 : 0.1}
            className="invert group-hover:invert-0"
          />
        </Button>
        <a
          href="/favoritos"
          aria-label="Favoritos"
          className="flex items-center justify-center w-10 h-10"
        >
          <Icon
            id="Heart"
            size={22}
            strokeWidth={1.5}
            fill="none"
            className={translucentEffect ? "text-white" : "text-[#1A1A1A]"}
          />
        </a>
        <MyAccount
          translucentEffect={translucentEffect}
          isSearchVisible={isSearchVisible}
        />
        {platform === "vtex" && (
          <CartButtonVTEX isSearchVisible={searchVisible} />
        )}
      </div>
    </div>
  );
}

export default DesktopSearchbar;
